/**
 * One spelling for a car's make, model and colour, whatever the RTA file or a hand edit typed:
 * "PEUGEOT" / "Peugeot" -> "Peugeot", "Citroën" / "CITROEN" -> "Citroen", "k5" -> "K5",
 * "Arizo 5" -> "Arrizo 5", "GREY" / "Grey" / "GRAY" -> "Gray".
 * Every fleet write goes through cleanCarNames (services/firestore fleetService); the one-time
 * cleanup of the old docs used the same rules.
 */
const MAKE_ACRONYMS = new Set(['BMW', 'KIA', 'MG', 'JAC', 'GMC', 'BYD']);
const MODEL_ACRONYMS = new Set(['GK', 'GS', 'GLX', 'STD', 'DEL', 'ES', 'UX', 'YOU', 'PRO', 'SUV', 'GT', 'EV']);
const MODEL_ALIASES = { 'ARIZO 5': 'ARRIZO 5', 'GRAND I 10': 'GRAND I10' };

const ascii = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '');
const squash = (s) => ascii(s).replace(/\s+/g, ' ').trim();
const title = (w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w);

/** One word (hyphen / slash parts each): digits -> upper ("k5" -> "K5", but "4x2" stays), one letter / acronym -> upper, else Title. */
const word = (w, acronyms) => w.split(/([-/])/).map((p) => {
    if (p === '-' || p === '/' || !p) return p;
    const up = p.toUpperCase();
    if (/^\d+x\d+$/i.test(p)) return p.toLowerCase();
    if (/\d/.test(p) || p.length === 1 || acronyms.has(up)) return up;
    return title(p);
}).join('');

const words = (s, acronyms) => squash(s).split(' ').map((w) => word(w, acronyms)).join(' ');

export const cleanMake = (v) => (squash(v) ? words(v, MAKE_ACRONYMS) : v);

export const cleanModel = (v) => {
    const s = squash(v);
    if (!s) return v;
    const alias = MODEL_ALIASES[s.toUpperCase()];
    return words(alias || s, MODEL_ACRONYMS);
};

/** Colour: Title Case; a plain grey is "Gray" (named greys such as "Phantom Grey" keep their name). */
export const cleanColor = (v) => {
    if (!squash(v)) return v;
    const c = words(v, new Set());
    return /^Gr[ae]y$/.test(c) ? 'Gray' : c;
};

/** The same car with its make / model / colour cleaned (only the fields it carries). */
export const cleanCarNames = (car) => {
    if (!car || typeof car !== 'object') return car;
    const out = { ...car };
    if (typeof out.make === 'string') out.make = cleanMake(out.make);
    if (typeof out.model === 'string') out.model = cleanModel(out.model);
    if (typeof out.color === 'string') out.color = cleanColor(out.color);
    return out;
};
